// Profile — codename, intel, spy avatar generation, comms + tracking.
import { el, mount, toast } from "../lib/dom";
import { currentUser, signOut, storage } from "../lib/firebase";
import { doc, setDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { ref, getDownloadURL } from "firebase/storage";
import { watchGuest, generateAvatar, type Guest } from "../lib/data";
import { uploadSelfie } from "../lib/upload";
import { surveillanceFilter } from "../lib/avatar-filter";
import { requestPush } from "../lib/permissions";
import { startSharing } from "../lib/location";
import { getPosition } from "../lib/geo";
import { installPrompt } from "../components/install-prompt";
import { webviewBanner } from "../components/webview-banner";
import { nav } from "../components/nav";
import { go } from "../router";
import { showHelpOverlay } from "../components/help-overlay";

let unsub: (() => void) | null = null;

export async function renderProfile(): Promise<void> {
  if (unsub) { unsub(); unsub = null; }
  const uid = currentUser()!.uid;

  const avatarEl = el("div.avatar-hero__media", {}, el("div.avatar-hero__fallback", {}, "🕵️"));
  const nameEl = el("h2.avatar-hero__name", {}, "…");
  const intelEl = el("div.avatar-hero__intel", {}, "0 ◆");
  const avatarStatusEl = el("p.tiny.dim.center", { style: "margin:10px 0 0" });
  const hero = el("div.avatar-hero", {},
    avatarEl,
    el("div.avatar-hero__scrim", {}, nameEl, intelEl),
  );

  // Hidden file input reused for avatar capture.
  const avatarFile = el("input", {
    type: "file", accept: "image/*", capture: "user", style: "display:none",
  }) as HTMLInputElement;

  const genBtn = el("button.btn", {}, "Generate spy avatar") as HTMLButtonElement;
  const pushBtn = el("button.btn.btn--ghost", {}, "Enable secure comms (notifications)") as HTMLButtonElement;
  const gpsBtn = el("button.btn.btn--ghost", {}, "Enable field tracking (GPS)") as HTMLButtonElement;
  const signOutBtn = el("button.btn.btn--danger", {}, "Sign out") as HTMLButtonElement;

  let currentGuest: Guest | null = null;

  async function showAvatar(path: string | null) {
    const fallback = () =>
      avatarEl.replaceChildren(el("div.avatar-hero__fallback", {}, "🕵️"));
    if (!path) { fallback(); return; }
    try {
      const url = await getDownloadURL(ref(storage, path));
      avatarEl.replaceChildren(el("img", { src: url }));
    } catch {
      fallback();
    }
  }

  genBtn.addEventListener("click", () => avatarFile.click());

  avatarFile.addEventListener("change", async () => {
    const file = avatarFile.files?.[0];
    if (!file) return;
    genBtn.disabled = true;
    genBtn.textContent = "Processing…";
    try {
      // Upload source selfie.
      const { path } = await uploadSelfie(uid, await import("../lib/upload").then((m) => m.compressImage(file)), "avatars");
      try {
        const res = await generateAvatar({ sourcePath: path });
        if (!res.ok) throw new Error("generation failed");
        toast("Avatar dispatched to the lab. Stand by.", "ok");
      } catch {
        // Fallback: client-side surveillance filter.
        toast("Lab offline — applying field filter.", "warn");
        const blob = await surveillanceFilter(file);
        const up = await uploadSelfie(uid, blob, "avatars");
        await setDoc(
          doc(db, "guests", uid),
          { avatarPath: up.path, avatarStatus: "ready" },
          { merge: true },
        );
        // Publish to the surveillance feed (best-effort).
        try {
          await addDoc(collection(db, "photos"), {
            guestId: uid,
            codename: currentGuest?.displayName ?? "AGENT",
            storagePath: up.path,
            downloadURL: up.url,
            caption: "COVER IDENTITY ACTIVE",
            type: "agentJoined",
            createdAt: serverTimestamp(),
          });
        } catch { /* best-effort */ }
      }
    } catch (e: unknown) {
      toast((e as { message?: string })?.message ?? "Avatar failed.", "err");
    } finally {
      genBtn.disabled = false;
      genBtn.textContent = "Generate spy avatar";
      avatarFile.value = "";
    }
  });

  pushBtn.addEventListener("click", async () => {
    pushBtn.disabled = true;
    const ok = await requestPush(uid);
    toast(ok ? "Secure comms online." : "Comms unavailable on this device.", ok ? "ok" : "warn");
    pushBtn.disabled = false;
    if (ok) pushBtn.textContent = "Secure comms enabled ✓";
  });

  gpsBtn.addEventListener("click", async () => {
    gpsBtn.disabled = true;
    gpsBtn.textContent = "Acquiring signal…";
    // Verify we can actually get a fix before claiming success.
    const p = await getPosition();
    if (p) {
      startSharing(uid, currentGuest?.displayName ?? null, (reason) => {
        toast(reason === "denied"
          ? "Tracking denied — re-enable location for this site in browser settings."
          : "GPS signal lost. Try again outdoors.", "warn");
        gpsBtn.disabled = false;
        gpsBtn.textContent = "Enable field tracking (GPS)";
      });
      toast("Field tracking engaged.", "ok");
      gpsBtn.textContent = "Field tracking enabled ✓";
      gpsBtn.disabled = false;
    } else {
      toast("Location refused or unavailable. Check browser settings and retry.", "warn");
      gpsBtn.disabled = false;
      gpsBtn.textContent = "Enable field tracking (GPS)";
    }
  });

  signOutBtn.addEventListener("click", async () => {
    await signOut();
    go("/activate");
  });

  unsub = watchGuest(uid, (g) => {
    currentGuest = g;
    nameEl.textContent = g?.displayName ?? "Unknown Agent";
    intelEl.textContent = `${g?.totalIntel ?? 0} ◆`;
    const status = g?.avatarStatus ?? "none";
    avatarStatusEl.textContent =
      status === "pending" ? "Avatar in the lab…"
      : status === "ready" ? "Cover identity active."
      : status === "failed" ? "Generation failed — try again."
      : "No cover identity on file.";
    void showAvatar(g?.avatarPath ?? null);
  });

  const banner = webviewBanner();

  mount(el("div", {},
    banner,
    el("div.card", {},
      hero,
      avatarStatusEl,
    ),
    el("div.card", {},
      el("span.card__tag", {}, "COVER IDENTITY"),
      genBtn,
      avatarFile,
    ),
    el("div.card", {},
      el("span.card__tag", {}, "FIELD KIT"),
      pushBtn,
      el("div", { style: "height:8px" }),
      gpsBtn,
      el("div", { style: "height:8px" }),
      installPrompt({ compact: true }),
    ),
    el("div.card", {},
      signOutBtn,
    ),
    nav("/profile"),
  ));

  // Show the first-visit help overlay once (skips if already seen).
  showHelpOverlay();
}
